// Main application bootstrap with Alpine.js
// @ts-expect-error Alpine global
import Alpine from 'alpinejs';
import { Router } from './app/router';
import { ScreenController } from './ui/screenController';
import { store } from './app/store';
import { initAnalytics } from './app/analytics';
import { initStatsig, FLAGS } from './app/statsig';
import { BillingService } from './domain/billing-service';
import { initMetaPixel } from './infra/metaPixel';
import { getEnvConfig } from './config/env';
import copyData from './config/copy.en.json';

// Make Alpine available globally
window.Alpine = Alpine;

// Initialize billing service globally
let billingService: BillingService | null = null;

// Application initialization
async function boot() {
  console.log('🚀 Lunai Care Web2App Starting...');

  // Get preloader element
  const preloader = document.getElementById('preloader');

  try {
    // Show preloader is already visible by default

    // Load copy/localization data
    Alpine.store('copy', copyData);

    // Initialize Alpine stores
    Alpine.store('userData', {});
    Alpine.store('flags', {});

    // Start Alpine
    Alpine.start();

    // Get app container
    const appContainer = document.getElementById('app');
    if (!appContainer) {
      console.error('App container not found');
      hidePreloader(preloader);
      return;
    }

    // Get or create user ID
    const userId = getOrCreateUserId();

    // Initialize analytics (with minimum delay to show preloader)
    await Promise.all([
      initAnalytics(),
      // Add minimum display time for preloader (1.5 seconds)
      new Promise(resolve => setTimeout(resolve, 1500))
    ]);

    // Initialize Statsig for feature flags
    await initStatsig(userId);

    // Initialize Meta Pixel
    await initMetaPixel();

    // Initialize Billing Service with environment configuration
    const envConfig = getEnvConfig();
    billingService = new BillingService({
      paddle: {
        clientToken: envConfig.paddle.clientToken,
        sandboxClientToken: envConfig.paddle.clientToken,
        vendorId: envConfig.paddle.vendorId,
        sandboxVendorId: envConfig.paddle.vendorId
      },
      stripe: envConfig.stripe ? {
        publicKey: envConfig.stripe.publicKey,
        sandboxPublicKey: envConfig.stripe.publicKey
      } : undefined
    });

    // Initialize screen controller
    const screenController = new ScreenController(appContainer);

    // Initialize router
    const router = new Router(screenController);

    // Set up navigation event listeners using PRD pattern
    document.addEventListener('ui:navigate', (event) => {
      const direction = event.detail?.direction;
      if (direction === 'next') {
        router.navigateNext();
      } else if (direction === 'back') {
        router.navigateBack();
      } else if (event.detail?.screenId) {
        router.navigateTo(event.detail.screenId);
      }
    });

    // Provide current screen info to screen controller
    document.addEventListener('app:getCurrentScreen', (event) => {
      event.detail = { screen: router.getCurrentScreen() };
    });

    // Track screen views
    document.addEventListener('app:screenView', (event) => {
      console.log('Screen view:', event.detail);
      // Send to analytics service
    });

    // Initialize Paddle when needed
    let paddleInitialized = false;
    document.addEventListener('infra:paddleInit', async () => {
      if (!paddleInitialized && billingService) {
        await billingService.initializeProvider('paddle');
        paddleInitialized = true;
      }
    });

    // Handle checkout
    document.addEventListener('ui:checkout', () => {
      openCheckout();
    });

    // Handle timer expiration
    document.addEventListener('app:timerExpired', () => {
      handleTimerExpired();
    });

    // Start router
    router.init();

    // Hide preloader after everything is initialized
    hidePreloader(preloader);

    // Log initial state
    console.log('📱 App initialized successfully');
    console.log('📊 User data:', store.getAll());
  } catch (error) {
    console.error('Error during initialization:', error);
    // Hide preloader even on error
    hidePreloader(preloader);
  }
}

// Hide preloader with animation
function hidePreloader(preloader: HTMLElement | null) {
  if (preloader) {
    preloader.classList.add('hidden');
    // Remove from DOM after animation completes
    setTimeout(() => {
      preloader.style.display = 'none';
    }, 500);
  }
}

// Open checkout using BillingService
async function openCheckout() {
  if (!billingService) {
    console.error('BillingService not initialized');
    return;
  }

  // Get current provider (ensure Paddle is initialized)
  let provider = billingService.getCurrentProvider();
  if (!provider) {
    // Initialize Paddle if not already done
    await billingService.initializeProvider('paddle');
    provider = billingService.getCurrentProvider();
  }

  if (!provider) {
    console.error('Failed to initialize billing provider');
    return;
  }

  // Get product ID for current offer (default to annual plan)
  const productId = 'com.shamruk.velvet.y40'; // Annual plan
  const email = store.get('email') as string;

  // Open checkout
  try {
    await provider.openCheckout({
      priceId: productId,
      email: email,
      successUrl: window.location.origin + '/#success',
      customData: {
        user_id: localStorage.getItem('lunai_user_id'),
        source: 'web2app',
        funnel_version: 'v5'
      }
    }, 'popup'); // Use popup mode for webview compatibility
  } catch (error) {
    console.error('Failed to open checkout:', error);
  }
}

// Handle timer expiration
function handleTimerExpired() {
  // Could show different pricing or messaging
  console.log('⏰ Timer expired - showing regular pricing');
}

// Get or create anonymous user ID
function getOrCreateUserId() {
  const key = 'lunai_user_id';
  let userId = localStorage.getItem(key);

  if (!userId) {
    userId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem(key, userId);
  }

  return userId;
}

// Wait for DOM to be ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

// Export for debugging
window.lunaiDebug = {
  store,
  getState: () => store.getAll(),
  clearState: () => store.clear(),
  navigateTo: (screenId) => {
    document.dispatchEvent(new CustomEvent('navigation:to', { detail: { screenId } }));
  }
};